// import TagManager from 'react-gtm-module';
import PhoneCode from 'pages/Registration/PhoneCode/PhoneCode';
import { ReactNode } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router';
import DocumentUploadLater from 'pages/Registration/DocumentUploadLater/DocumentUploadLater';

import OfflinePSD2Token from 'pages/Registration/OfflinePSD2Token/OfflinePSD2Token';
import ContinueAffiliate from 'pages/ContinueAffiliate/ContinueAffiliate';
import OfflinePSD2Verified from 'pages/Registration/OfflinePSD2Verified/OfflinePSD2Verified';
import OfflinePSD2Error from 'pages/Registration/OfflinePSD2Error/OfflinePSD2Error';
import Downsale from 'pages/Registration/Downsale/Downsale';
import RejectedOffer from 'pages/Registration/RejectedOffer/RejectedOffer';
import Agreement from 'pages/Registration/Agreement/Agreement';
import OCRResults from 'pages/Registration/OCRResults/OCRResults';
import Manual from 'pages/Registration/ManualOCR/Manual';
import PSD2Guide from 'pages/Registration/PSD2Guide/PSD2Guide';
import PSD2Consents from 'pages/Registration/PSD2Consents/PSD2Consents';
import PSD2Receiving from 'pages/Registration/PSD2Receiving/PSD2Receiving';
import PSD2Verified from 'pages/Registration/PSD2Verified/PSD2Verified';
import PSD2Error from 'pages/Registration/PSD2Error/PSD2Error';
import ProofIncome from 'pages/Registration/ProofIncome/ProofIncome';
import { useGoogleTagManager } from 'hooks/useGoogleTagManager';
import Waiting from 'pages/Waiting/Waiting';
import ManualReview from 'pages/Registration/ManualReview/ManualReview';
import BackButtonBlocker from 'utils/BackButtonBlocker';

import Finance from 'pages/Registration/Finance/Finance';

import Login from 'pages/Login/Login';
import DataLayer from 'utils/dataLayer/DataLayer';
import WelcomeBackIdentificationStatus from 'pages/Registration/WelcomeBack/WelcomeBackIdentificationStatus';
import WelcomeBackNewApplication from 'pages/Registration/WelcomeBack/WelcomeBackNewApplication';
import PSD2BanksList from 'pages/Registration/PSD2Banks/PSD2Banks';
import PSD2AccountSelection from 'pages/Registration/PSD2SelectBankAccount/PSD2AccountSelection';
import UpsaleChangeAmount from 'pages/Registration/UpsaleChangeAmount/UpsaleChangeAmount';
import ManualOCR from 'pages/Registration/ManualOCR/ManualOCR';
import ManualUploadID from 'pages/Registration/ManualUploadID/ManualUploadID';
import AffiliatePage from 'pages/Affiliate/Affiliate';
import PageTracker from 'hooks/context/EventCollector/usePageTracker';
import PersonalDetailsContinue from 'pages/Registration/PersonalDetails/PersonalDetailsContinue/PersonalDetailsContinue';
import DashboardRedirect from 'pages/Login/DashboardRedirect';
import BankIdWaiting from 'pages/Registration/BankId/BankIdWaiting/BankIdWaiting';
import { AsyncOneTimeTokenLogin } from 'pages/Login/async';
import { PersonalDetailsResolver } from 'pages/Registration/PersonalDetails/PersonalDetailsRoute';
import BankIdAccountSelection from 'pages/Registration/BankId/BankIdAccountSelection/BankIdAccountSelection';
import { TestHelperWrapper } from 'utils/TestHelper/TestHelperWrapper';
import {
  AdjustIncome,
  Approved,
  BankIdConfirm,
  BankIdError,
  BankIdInfo,
  Intro,
  NotFound,
  PhoneChange,
  PhoneVerification,
  Registration,
  Upsale,
} from '../pages';
import styles from './Router.module.scss';
import * as R from './routes';
// import { VCC_LIVE_CHAT } from '../constants';
// import { VccLiveChat } from '../components';

function RouteWrapper() {
  // const isChatEnabled = import.meta.env.VITE_CHAT_ENABLED !== 'false';
  return (
    <>
      <BackButtonBlocker />
      <PageTracker />
      <DataLayer />
      <Outlet />
      <TestHelperWrapper />
      {/* isChatEnabled && <VccLiveChat accountId={VCC_LIVE_CHAT.ACCOUNT_ID} /> */}
    </>
  );
}

function MultiRoute(paths: string[], component: ReactNode) {
  return paths.map((path) => (
    <Route key={path} element={component} path={path} />
  ));
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RouteWrapper />}>
      {/* Route should not be used as redirect specified on login itself based on user state */}
      <Route path={R.DASHBOARD_REDIRECT} element={<DashboardRedirect />} />
      <Route
        path={R.LEGACY_LOGIN_ONE_TIME_TOKEN_LOGIN}
        element={<AsyncOneTimeTokenLogin />}
      />
      <Route path={R.LOGIN} element={<Login />} />
      <Route element={<OfflinePSD2Token />} path={R.OFFLINE_TOKEN} />
      <Route element={<AffiliatePage />} path={R.AFFILIATE} />
      <Route element={<Registration />} path="/">
        <Route
          element={<WelcomeBackIdentificationStatus />}
          path={R.WELCOME_BACK}
        />
        <Route
          element={<WelcomeBackNewApplication />}
          path={R.WELCOME_BACK_NEW}
        />
        {MultiRoute([R.INTRO, R.INTRO_START_APP, R.INTRO_2], <Intro />)}
        <Route element={<ContinueAffiliate />} path={R.CONTINUE_AFFILIATE} />
        <Route
          element={<PersonalDetailsResolver />}
          path={R.PERSONAL_DETAILS}
        />
        <Route
          element={<PersonalDetailsContinue />}
          path={R.PERSONAL_DETAILS_CONTINUE}
        />
        <Route element={<Finance />} path={R.FINANCE} />
        <Route
          element={<PSD2BanksList />}
          path={`${R.OFFLINE_PSD2_BANKS}/:token`}
        />
        <Route
          path={`${R.OFFLINE_PSD2_GUIDE}/:token`}
          element={<PSD2Guide />}
        />
        <Route
          path={`${R.OFFLINE_PSD2_CONSENTS}/:token`}
          element={<PSD2Consents />}
        />
        {/* NOTE: Workaround because of back-end */}
        <Route
          path={R.OFFLINE_PSD2_BANK_ACCOUNT_SELECT}
          element={<PSD2AccountSelection />}
        />
        <Route
          path={`${R.OFFLINE_PSD2_BANK_ACCOUNT_SELECT}/:token`}
          element={<PSD2AccountSelection />}
        />
        {/* NOTE: Workaround because of back-end */}
        <Route path={R.OFFLINE_PSD2_RECEIVING} element={<PSD2Receiving />} />
        <Route
          path={`${R.OFFLINE_PSD2_RECEIVING}/:token`}
          element={<PSD2Receiving />}
        />
        <Route
          path={`${R.OFFLINE_PSD2_VERIFIED}/:token`}
          element={<OfflinePSD2Verified />}
        />
        <Route
          path={`${R.OFFLINE_PSD2_ERROR}/:token`}
          element={<OfflinePSD2Error />}
        />
        <Route path={R.BANK_ID} element={<BankIdInfo />} />
        <Route path={R.BANK_ID_CONFIRM} element={<BankIdConfirm />} />
        <Route path={R.BANK_ID_WAIT} element={<BankIdWaiting />} />
        <Route
          path={R.BANK_ID_ACCOUNT_SELECTION}
          element={<BankIdAccountSelection />}
        />
        <Route path={R.BANK_ID_ERROR} element={<BankIdError />} />
        <Route element={<PhoneChange />} path={R.PHONE_CHANGE} />
        <Route element={<PhoneVerification />} path={R.PHONE_VERIFICATION} />
        <Route element={<ManualOCR />} path={R.MANUAL_OCR} />
        <Route element={<Downsale />} path={R.DOWNSALE} />
        <Route element={<Upsale />} path={R.UPSALE} />
        <Route element={<UpsaleChangeAmount />} path={R.UPSALE_CHANGE_AMOUNT} />
        <Route element={<RejectedOffer />} path={R.REJECTED_OFFER} />
        <Route element={<Agreement />} path={R.CONSENT} />
        <Route element={<OCRResults />} path={R.OCR_RESULTS} />
        <Route element={<ManualUploadID />} path={R.MANUAL_UPLOAD_ID} />
        <Route element={<Manual />} path={R.MANUAL_IDENTIFICATION} />
        <Route element={<PSD2BanksList />} path={R.MANUAL_PSD2_BANKS} />
        <Route path={R.MANUAL_PSD2_GUIDE} element={<PSD2Guide />} />
        <Route path={R.MANUAL_PSD2_CONSENTS} element={<PSD2Consents />} />
        <Route element={<PSD2BanksList />} path={R.PSD2_BANKS} />
        <Route path={R.PSD2_GUIDE} element={<PSD2Guide />} />
        <Route path={R.PSD2_CONSENTS} element={<PSD2Consents />} />
        <Route
          path={R.PSD2_BANK_ACCOUNT_SELECT}
          element={<PSD2AccountSelection />}
        />
        <Route path={R.PSD2_RECEIVING} element={<PSD2Receiving />} />
        <Route path={R.PSD2_VERIFIED} element={<PSD2Verified />} />
        <Route path={R.WAITING} element={<Waiting />} />
        <Route path={R.PSD2_ERROR} element={<PSD2Error />} />
        <Route element={<WelcomeBackIdentificationStatus />} path={R.PENDING} />
        <Route element={<ManualReview />} path={R.MANUAL_REVIEW} />
        <Route element={<ProofIncome />} path={R.INCOME_PROOF} />
        <Route element={<Approved />} path={R.APPROVED} />
        <Route
          element={<DocumentUploadLater />}
          path={R.DOCUMENT_UPLOAD_LATER}
        />
        <Route element={<AdjustIncome />} path={R.ADJUST_INCOME} />
        <Route path="/" element={<Navigate to={R.INTRO} />} />
        <Route element={<PhoneCode />} path={R.SIGN_AGREEMENT} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>,
  ),
);

export default function AppRouter() {
  useGoogleTagManager();

  return (
    <div className={styles.wrapper}>
      <RouterProvider router={router} />
    </div>
  );
}
